/**
 * Website-monitoring API client (pirx-8mm1n, Phase 0).
 *
 * Thin wrappers over the `/api/monitoring/*` routes. Types are co-located here
 * (rather than in the shared `@/types` barrel) because the whole surface is a
 * flag-gated vertical slice; promote them if monitoring becomes first-class.
 *
 * Date-typed columns arrive as ISO strings over JSON, hence `string | null`.
 */

import type { RiskLevel } from "@/types";
import { request, requestVoid } from "@/services/api/request";

/** v1 source discriminator. Extend as adapters are added. */
export type MonitoredSourceType = "url";

export type MonitoredSourceStatus =
  | "draft"
  | "active"
  | "paused"
  | "failed"
  | "archived";

export type MonitoringRunStatus =
  | "captured"
  | "skipped_unchanged"
  | "analyzing"
  | "completed"
  | "failed";

/** A risk-tier transition between two completed runs (Phase 2 trend delta). */
export interface RiskLevelChange {
  from: RiskLevel;
  to: RiskLevel;
}

/** Deterministic "what changed" summary on a completed run. */
export interface DeltaSummary {
  /** `practice_type_ids` present in this run but not the previous (Phase E). */
  added: string[];
  /** `practice_type_ids` present in the previous run but not this one (Phase E). */
  removed: string[];
  riskLevelChanged: RiskLevelChange | null;
}

/** A single captured content block, surfaced in the capture preview. */
export interface CapturedElement {
  kind: "heading" | "paragraph" | "list" | "image_alt" | "link";
  text: string;
}

/** Public preview projection returned by `POST /monitoring/preview`. */
export interface CapturePreview {
  ok: boolean;
  title: string;
  charCount: number;
  structuredElements: CapturedElement[];
  failureCode?: string;
  finalUrl?: string;
  fetchedAt: string;
}

/** Per-job notification rules (pirx-164cp.3). */
export interface AlertConfig {
  riskRise: boolean;
  newClaim: boolean;
  /** Score floor (0–100); null = threshold rule off. */
  scoreThreshold: number | null;
  digest: boolean;
}

export type MonitoringAlertType =
  | "risk_rise"
  | "new_claim"
  | "score_threshold"
  | "digest";

/** A fired alert (serialized `monitoring_alerts` row). */
export interface MonitoringAlert {
  id: string;
  monitoredSourceId: string;
  monitoringRunId: string;
  alertType: MonitoringAlertType;
  payload: Record<string, unknown> | null;
  createdAt: string;
}

/** A monitoring job (serialized `monitored_sources` row). */
export interface MonitoredSource {
  id: string;
  userId: string;
  ownerId: string | null;
  teamId: string | null;
  sourceType: MonitoredSourceType;
  config: Record<string, unknown>;
  label: string | null;
  status: MonitoredSourceStatus;
  intervalMinutes: number;
  baselineHash: string | null;
  lastRunAt: string | null;
  lastSuccessAt: string | null;
  nextRunAt: string | null;
  consecutiveFailures: number;
  attestationAt: string | null;
  previewConfirmedAt: string | null;
  ownershipVerifiedAt: string | null;
  alertConfig: AlertConfig | null;
  createdAt: string;
  updatedAt: string;
}

/** One scheduler fire (serialized `monitoring_runs` row) — the trend ledger. */
export interface MonitoringRun {
  id: string;
  monitoredSourceId: string;
  runStatus: MonitoringRunStatus;
  auditId: string | null;
  contentHash: string | null;
  previousHash: string | null;
  changed: boolean | null;
  charCount: number | null;
  failureCode: string | null;
  /**
   * Object-store key of the captured page render (pirx-164cp.4). Null until a
   * render provider captures one; the screenshot feature ships behind the OFF
   * `WEBSITE_MONITORING` flag, so this is null in every live environment today.
   * Resolve a viewable URL with `getMonitoringRunScreenshotUrl(id)`.
   */
  screenshotKey: string | null;
  complianceScore: number | null;
  overallRiskLevel: RiskLevel | null;
  scoreDelta: number | null;
  deltaSummary: DeltaSummary | null;
  scheduledFor: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

export interface MonitoringPreviewInput {
  sourceType: MonitoredSourceType;
  config: { url: string };
}

export interface CreateMonitoredSourceInput {
  sourceType: MonitoredSourceType;
  config: { url: string };
  label?: string;
  intervalMinutes: number;
  attestation: true;
  previewConfirmed: true;
  context?: Record<string, unknown>;
  alertConfig?: AlertConfig;
}

export interface UpdateMonitoredSourceInput {
  label?: string;
  intervalMinutes?: number;
  /** `null` clears the rules; omit to leave them untouched. */
  alertConfig?: AlertConfig | null;
}

/** Dry-run capture — "what you preview is what gets monitored". */
export function previewMonitorCapture(
  input: MonitoringPreviewInput,
): Promise<CapturePreview> {
  return request<CapturePreview>("/monitoring/preview", {
    method: "POST",
    body: input,
    contentType: "json",
  });
}

export function createMonitoredSource(
  input: CreateMonitoredSourceInput,
): Promise<MonitoredSource> {
  return request<MonitoredSource>("/monitoring/sources", {
    method: "POST",
    body: input,
    contentType: "json",
  });
}

export function listMonitoredSources(): Promise<MonitoredSource[]> {
  return request<MonitoredSource[]>("/monitoring/sources");
}

export function getMonitoredSource(id: string): Promise<MonitoredSource> {
  return request<MonitoredSource>(`/monitoring/sources/${id}`);
}

export function listMonitoringRuns(id: string): Promise<MonitoringRun[]> {
  return request<MonitoringRun[]>(`/monitoring/sources/${id}/runs`);
}

export function listMonitoringAlerts(id: string): Promise<MonitoringAlert[]> {
  return request<MonitoringAlert[]>(`/monitoring/sources/${id}/alerts`);
}

/**
 * Resolve a short-lived signed URL for a run's stored screenshot (pirx-164cp.4).
 * 404s when the run has no stored render (the case in every live environment
 * today, until a render provider lands).
 */
export function getMonitoringRunScreenshotUrl(
  runId: string,
): Promise<{ url: string }> {
  return request<{ url: string }>(`/monitoring/runs/${runId}/screenshot`);
}

export function updateMonitoredSource(
  id: string,
  input: UpdateMonitoredSourceInput,
): Promise<MonitoredSource> {
  return request<MonitoredSource>(`/monitoring/sources/${id}`, {
    method: "PATCH",
    body: input,
    contentType: "json",
  });
}

/** Fire a source's monitor immediately (out of band from the schedule). */
export function runMonitoredSource(id: string): Promise<MonitoringRun> {
  return request<MonitoringRun>(`/monitoring/sources/${id}/run`, {
    method: "POST",
  });
}

export function pauseMonitoredSource(id: string): Promise<MonitoredSource> {
  return request<MonitoredSource>(`/monitoring/sources/${id}/pause`, {
    method: "POST",
  });
}

export function resumeMonitoredSource(id: string): Promise<MonitoredSource> {
  return request<MonitoredSource>(`/monitoring/sources/${id}/resume`, {
    method: "POST",
  });
}

export function deleteMonitoredSource(id: string): Promise<void> {
  return requestVoid(`/monitoring/sources/${id}`, { method: "DELETE" });
}
